/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Plugins.AlertsManager {
    'use strict';

    import IAlert = Meevo.Model.IAlert;
    import VerbEnum = Meevo.Model.VerbEnum;
    import NounEnum = Meevo.Model.NounEnum;
    import SecurityKeys = Meevo.Components.Common.SecurityKeys;
    import SecurityParts = Meevo.Components.Common.SecurityParts;
    import IAlertUserListItem = Meevo.Model.IAlertUserListItem;
    import IAlertCatListItem = Meevo.Model.IAlertCatListItem;
    import IAlertListItem = Meevo.Model.IAlertListItem;
    import NotificationMethodEnum = Meevo.Model.NotificationMethodEnum;
    import IsForAllEnum = Meevo.Model.IsForAllEnum;

    interface IParams {
        view: DisplayViewsEnum;
        criteria: IAlertCriteria;
    }

    interface ISecurityKeys {
        create: string;
        write: string;
        delete: string;
        employeeUserFilterFieldSecurity: string;
        employeeSelection: string;
    }

    interface ICriteriaTypeAhead {
        user: IAlertUserListItem,
        category: IAlertCatListItem;
    }

    type TAlertListItem = IAlertUserListItem | IAlertCatListItem;

    interface IAlertsListController {
        params: IParams;
        securityKeys: ISecurityKeys;

        employeePages: Object;
        categoryPages: Object;
        selectedItem: TAlertListItem;
        alertCriteria: Object;
        criteriaTypeAhead: ICriteriaTypeAhead;

        defaultCriteria: () => IAlertCriteria;
        onPageLoad: (isNewPage?: boolean) => void;

        getAlertList: (item, pane) => void;
        showAddAlertForm: (isUsageTracked?: boolean) => void;
        criteriaChanged: () => void;
        getAlertCount: (list: Array<any>) => ng.IPromise<any>;
        editAlert: (alert: IAlertListItem) => void;
        deleteAlert: (alert: IAlertListItem) => void;
        // for pager
        forcePageLoading: (criteria: IAlertCriteria) => void;
        checkFlagToAll: (flag: IsForAllEnum) => boolean;
    }

    class AlertsListController implements IAlertsListController {

        static $inject = [
            '$scope',
            '$rootScope',
            '$state',
            'meevo.plugins.alertsManager.AlertsManagerService',
            'meevo.components.busy.BusyService',
            '$q',
            'meevo.components.common.DataService',
            'meevo.AppSettings',
            'meevo.components.common.IdentityService'
        ];
        private _currentUser = this.identityService.current();
        public params: IParams = {
            view: DisplayViewsEnum.Employee,
            criteria: {
                cat_SId: null,
                user_TId: this._currentUser.userId
            }
        }
        public securityKeys: ISecurityKeys = {
            create: SecurityKeys.MANAGEMENT_ALERTSMANAGER + SecurityParts.KEY_PART_CREATE,
            write: SecurityKeys.MANAGEMENT_ALERTSMANAGER + SecurityParts.KEY_PART_WRITE,
            delete: SecurityKeys.MANAGEMENT_ALERTSMANAGER + SecurityParts.KEY_PART_DELETE,
            employeeUserFilterFieldSecurity: SecurityKeys.MANAGEMENT_ALERTSMANAGER_FIELD_EMPLOYEEUSERFILTER,
            employeeSelection: SecurityKeys.MANAGEMENT_ALERTSMANAGER_FIELD_EMPLOYEE
        };

        public employeePages;
        public categoryPages;
        public selectedItem: TAlertListItem;
        public alertCriteria = {
            itemsPerPage: 15,
            criteria: {
                objectStateEnum: Meevo.Model.ObjectStateEnum.Active,
                user_TId: this._currentUser.userId,
                cat_SId: null
            }
        };
        public criteriaTypeAhead: ICriteriaTypeAhead = {
            user: {
                employeeCode: this._currentUser.userName,
                user_TId: this._currentUser.userId
            },
            category: null
        };

        constructor(
            protected $scope: angular.IScope,
            protected $rootScope: ng.IRootScopeService,
            protected $state: angular.ui.IState,
            protected alertsManagerService: IAlertsManagerService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected $q: angular.IQService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected identityService: Meevo.Components.Common.IIdentityService
        ) {
            this.$scope.$on('alertsManagerAccordion:onExpandAnimationEnd', () => {
                let url = `${this.appSettings.API_ENDPOINTS.alertsManager}`;

                if (this.selectedItem.alertList) return;

                this.selectedItem.alertList = new Meevo.Components.BaseClasses.PagedResults(this.dataService, url, (pageNumber: number, items: Array<IAlert>) => {
                    items.forEach((item) => {
                        item.oncePerDay = (item.notificationMethod & NotificationMethodEnum.OnceDay) === NotificationMethodEnum.OnceDay;
                        item.sms = (item.notificationMethod & NotificationMethodEnum.Sms) === NotificationMethodEnum.Sms;
                        item.email = (item.notificationMethod & NotificationMethodEnum.Email) === NotificationMethodEnum.Email;
                    });
                });
                this.selectedItem.alertList.initSearch(this.alertCriteria);
            });
        }

        /**
         * Get criteria for Pager
         */
        defaultCriteria(): IAlertCriteria {
            return this.params.criteria;
        }

        /**
         * Callback from Pager when selected page is loaded
         * @param {boolean} isNewPage
         */
        onPageLoad(isNewPage: boolean) {
            let key = this.params.view === DisplayViewsEnum.Employee ? 'employeePages' : 'categoryPages',
                pageObject = this[key];

            if (isNewPage) {
                // update count
                if (!pageObject.pages[pageObject.currentPage]) return; // todo: dev

                this.busyService.start();
                this.alertsManagerService.usageTrackingLog(NounEnum.AlertManagerPage, VerbEnum.SelectionCriteriaChanged);
                this.getAlertCount(pageObject.pages[pageObject.currentPage])
                    .finally(() => {
                        this.busyService.stop();
                    });
            } else {
                // collapse panes
                pageObject.pages[pageObject.currentPage].forEach((e) => {
                    e.isExpanded = false;
                });
            }
        }

        /**
         * Get Alerts
         * @param item
         * @param pane
         */
        getAlertList(item, pane): void {
            let employeeView = this.params.view === DisplayViewsEnum.Employee;

            this.selectedItem = item;
            this.alertCriteria.criteria.user_TId = employeeView ? item.user_TId : this.params.criteria.user_TId;
            this.alertCriteria.criteria.cat_SId = employeeView ? this.params.criteria.cat_SId : item.cat_SId;

            pane.toggle();
        }

        /**
         * Display form for adding an Alert
         */
        showAddAlertForm(isUsageTracked?: boolean): void {
            if (isUsageTracked) {
                this.alertsManagerService.usageTrackingLog(NounEnum.AlertManagerPage, VerbEnum.AddNewButtonClicked);
            }

            this.alertsManagerService.view = ViewEnum.AddNew;
        }

        /**
         * Filter List
         */
        criteriaChanged(): void {
            let catId = this.criteriaTypeAhead.category ? this.criteriaTypeAhead.category.cat_SId : null,
                userId = this.criteriaTypeAhead.user ? this.criteriaTypeAhead.user.user_TId : null,
                doFetch: boolean = false;

            if (catId !== this.params.criteria.cat_SId) {
                this.params.criteria.cat_SId = catId;
                doFetch = true;
            }

            if (userId !== this.params.criteria.user_TId) {
                this.params.criteria.user_TId = userId
                doFetch = true;
            }

            if (doFetch) {
                this.forcePageLoading(this.params.criteria);
            }
        }

        /**
         * Get count of alerts
         */
        getAlertCount(list: Array<any>): ng.IPromise<any> {
            let promiseArr = [],
                employeeView = this.params.view === DisplayViewsEnum.Employee;
            for (let item of list) {
                promiseArr.push(this.alertsManagerService.getAlertCoun({
                    objectStateEnum: Meevo.Model.ObjectStateEnum.Active,
                    user_TId: employeeView ? item.user_TId : this.params.criteria.user_TId,
                    cat_SId: employeeView ? this.params.criteria.cat_SId : item.cat_SId
                }));
            }

            return this.$q.all(promiseArr)
                .then((countList: Array<number>) => {
                    countList.forEach((value, index) => {
                        list[index].count = value;
                    });
                })
        }

        /**
         * Edit Alert
         * @param {IAlertListItem} alert
         */
        editAlert(alert: IAlertListItem): void {
            this.busyService.start();
            this.alertsManagerService.usageTrackingLog(NounEnum.AlertManagerPage, VerbEnum.EditButtonClicked);
            this.alertsManagerService.getAlert(alert.id)
                .then((alert) => {
                    this.alertsManagerService.alert = alert;
                    this.showAddAlertForm();
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        /**
         * Delete Alert
         * @param {IAlertListItem} alert
         */
        deleteAlert(alert: IAlertListItem): void {
            let user_TId = alert.user_TId;

            this.alertsManagerService.usageTrackingLog(NounEnum.AlertManagerPage, VerbEnum.DeleteButtonClicked);
            this.busyService.start();
            this.alertsManagerService.getAlert(alert.id)
                .then((alert) => {
                    return this.alertsManagerService.deleteAlert(_.extend(alert, { user_TId }))
                        .then(() => {
                            this.forcePageLoading(this.params.criteria);
                        })
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        // for pager
        forcePageLoading(criteria: IAlertCriteria): void { }

        /**
         * Check if flag is IsForAllEnum.ToAll
         * @param {IsForAllEnum} flag
         */
        checkFlagToAll(flag: IsForAllEnum): boolean {
            return (flag & IsForAllEnum.ToAll) === IsForAllEnum.ToAll;
        }

        /**
         * Check if flag is IsForAllEnum.ToEmployee
         */
        checkFlagToEmployee(flag: IsForAllEnum): boolean {
            return (flag & IsForAllEnum.ToEmployee) === IsForAllEnum.ToEmployee;
        }

        get displayViewsEnum(): any {
            return DisplayViewsEnum;
        }
    }

    angular.module('meevo.plugins.alertsManager')
        .controller('meevo.plugins.alertsManager.AlertsListController', AlertsListController);
}